/**
 * Skip Confirmation Dialog Component
 *
 * Material-UI Dialog that asks for confirmation before skipping a tour.
 * Provides a better user experience by preventing accidental tour skips.
 *
 * @author Andres Nava
 * @version 1.0.0
 */

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import {
  WarningAmber as WarningIcon,
  Tour as TourIcon,
} from "@mui/icons-material";

/**
 * Skip Confirmation Dialog Props
 */
interface SkipConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when user confirms skip */
  onConfirm: () => void;
  /** Callback when user cancels skip */
  onCancel: () => void;
  /** Tour name for display */
  tourName?: string;
  /** Number of remaining steps */
  remainingSteps?: number;
  /** Mission checklist titles still incomplete */
  remainingStepTitles?: string[];
  /** Optional z-index override */
  zIndex?: number;
}

/**
 * Skip Confirmation Dialog Component
 *
 * Displays a confirmation dialog when user attempts to skip a tour.
 */
export const SkipConfirmationDialog: React.FC<SkipConfirmationDialogProps> = ({
  open,
  onConfirm,
  onCancel,
  tourName = "this tour",
  remainingSteps = 0,
  remainingStepTitles,
  zIndex,
}) => {
  const theme = useTheme();
  const resolvedZIndex = zIndex ?? theme.zIndex.modal + 1000;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="skip-dialog-title"
      aria-describedby="skip-dialog-description"
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        sx: {
          zIndex: resolvedZIndex,
          backgroundColor: "rgba(0, 0, 0, 0.75)",
        },
      }}
      sx={{
        zIndex: resolvedZIndex,
        "& .MuiDialog-container": {
          zIndex: resolvedZIndex,
        },
        "& .MuiDialog-paper": {
          zIndex: resolvedZIndex,
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 0,
          boxShadow: theme.shadows[24],
          position: "relative",
          zIndex: resolvedZIndex + 1,
        },
      }}
    >
      <DialogTitle
        id="skip-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: theme.palette.warning.light + "20",
            color: theme.palette.warning.main,
          }}
        >
          <WarningIcon />
        </Box>
        <Typography
          variant="h6"
          component="span"
          sx={{ fontWeight: theme.typography.fontWeightBold }}
        >
          Skip Tour?
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <DialogContentText
          id="skip-dialog-description"
          sx={{
            fontSize: "0.9375rem",
            lineHeight: 1.7,
            color: theme.palette.text.primary,
            mb: 2,
          }}
        >
          Are you sure you want to skip {tourName}?
          {remainingSteps > 0 && (
            <>
              {" "}
              You'll miss {remainingSteps}{" "}
              {remainingSteps === 1 ? "step" : "steps"} that could help you get
              started.
            </>
          )}{" "}
          If you skip now, this whole tour will be marked as skipped until you
          restart it from the tours menu.
        </DialogContentText>

        {remainingStepTitles && remainingStepTitles.length > 0 ? (
          <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 2 }}>
            {remainingStepTitles.map((title) => (
              <Typography component="li" variant="body2" key={title} sx={{ mb: 0.5 }}>
                {title}
              </Typography>
            ))}
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 2,
            borderRadius: 1,
            bgcolor: theme.palette.info.light + "10",
            borderLeft: `3px solid ${theme.palette.info.main}`,
          }}
        >
          <TourIcon
            sx={{
              color: theme.palette.info.main,
              fontSize: "1.5rem",
              mt: 0.25,
            }}
          />
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.info.main,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontSize: "0.6875rem",
                mb: 0.5,
                display: "block",
              }}
            >
              You can always restart
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              Don't worry! You can access this tour again from the onboarding
              menu at any time.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          gap: 1,
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: theme.shape.borderRadius,
            px: 2.5,
            py: 1,
            fontWeight: theme.typography.fontWeightMedium,
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "2px",
            },
          }}
        >
          Continue Tour
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="warning"
          sx={{
            textTransform: "none",
            borderRadius: theme.shape.borderRadius,
            px: 2.5,
            py: 1,
            fontWeight: theme.typography.fontWeightMedium,
            boxShadow: `0 2px 4px ${theme.palette.warning.main}30`,
            transition: theme.transitions.create(["transform", "box-shadow"], {
              duration: theme.transitions.duration.short,
            }),
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: `0 4px 8px ${theme.palette.warning.main}40`,
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.warning.main}`,
              outlineOffset: "2px",
            },
          }}
        >
          Skip Tour
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkipConfirmationDialog;
